<template>
  <div ui-view="" class="app-body" id="view">
    <page-header title="로그조회" description="사용자 활동(감사) 로그를 조회합니다." />

    <div class="page-body ph-30 book-search">
      <!-- 조회 모드 -->
      <div class="pt-20">
        <div class="row">
          <div class="search-item" v-for="m in modeOptions" :key="m.value">
            <button class="btn btn-md" :class="mode === m.value ? 'blue' : 'line-2'"
                    @click="changeMode(m.value)">{{ m.name }}</button>
          </div>
          <div class="search-item text-888" style="font-size:12px;">
            <template v-if="mode==='list'">조건에 맞는 로그를 목록으로 조회합니다.</template>
            <template v-else-if="mode==='freq'">동일 사용자가 단위시간당 임계값을 초과해 조회/다운로드한 건을 집계합니다.</template>
            <template v-else>권한 부여자(등급&gt;1) 현황을 팀·법인별로 집계합니다. (필터 무관)</template>
          </div>
        </div>
      </div>

      <!-- 검색 영역 (권한자 현황 모드는 필터 없음) -->
      <div class="pb-20" v-if="mode !== 'userLevels'">
        <div class="row">
          <div class="search-item" key="flt-date">
            <div class="input-group">
              <span class="input-group-addon title">기간</span>
              <DatePicker class="form-control" type="date" range v-model="dateRange"
                          style="width:240px; font-size: 12px;" :clearable="true" :editable="false"
                          input-class="calendar-input">
                <i slot="icon-calendar" class="icon-calendar" style="color:#222;cursor:pointer"></i>
              </DatePicker>
            </div>
          </div>

          <!-- 결과: 목록 모드 (고유 key 로 노드 재사용 방지 → 잔상/폭깨짐 방지) -->
          <div class="search-item" v-if="mode==='list'" key="flt-result">
            <div class="input-group">
              <dropdown title="결과" v-model="search.result"
                        :datasource="resultOptions" displayName="name" valueName="value"
                        :isSelectionFirstData="!search.result" />
            </div>
          </div>

          <!-- 단위: 빈도집계 모드 -->
          <div class="search-item" v-if="mode==='freq'" key="flt-unit">
            <div class="input-group">
              <dropdown title="단위" v-model="freq.unit"
                        :datasource="unitOptions" displayName="name" valueName="value"
                        :isSelectionFirstData="false" />
            </div>
          </div>
          <!-- 임계값: 빈도집계 모드 -->
          <div class="search-item" v-if="mode==='freq'" key="flt-threshold">
            <div class="input-group">
              <span class="input-group-addon title">임계값</span>
              <input type="number" min="1" class="form-control" v-model.number="freq.threshold" style="width:90px;">
              <span class="input-group-addon">건 초과</span>
            </div>
          </div>
        </div>

        <!-- 작업 다중 선택 (표준 체크박스 드롭다운) -->
        <div class="row mt-10">
          <div class="search-item">
            <div class="input-group">
              <span class="input-group-addon title">작업</span>
              <dropdown ref="actionDropdown" v-model="actionModel" :datasource="actionDs"
                        displayName="label" valueName="value" placeholder="작업 선택(다중)"
                        :showCheckbox="true" :isMultiple="true" :isAuto="true"
                        @checkBoxChange="onActionCheck" />
              <span class="input-group-addon">{{ isAllChecked ? '전체' : (selectedActions.length + '개') }}</span>
            </div>
          </div>
          <div class="search-item"><button class="btn btn-md line-2" @click="setActions('sensitive')">민감작업</button></div>
          <div class="search-item"><button class="btn btn-md line-2" @click="setActions('download')">다운로드</button></div>
          <div class="search-item"><button class="btn btn-md line-2" @click="setActions('all')">전체</button></div>
        </div>

        <!-- 목록 모드 부가 필터 -->
        <div class="row mt-10" v-if="mode==='list'">
          <div class="search-item">
            <label class="md-check" style="align-self:center;">
              <input type="checkbox" v-model="offHours"><i class="blue"></i>업무외시간만 (평일 22~06시·주말·공휴일)
            </label>
          </div>
          <div class="search-item" v-if="offHours">
            <div class="input-group">
              <span class="input-group-addon title">공휴일</span>
              <input type="text" class="form-control" v-model="holidays"
                     placeholder="YYYY-MM-DD, 쉼표구분" style="width:260px;">
              <button class="btn btn-md line-2" @click="fetchHolidays" :disabled="holidayLoading">
                {{ holidayLoading ? '…' : '공휴일 자동' }}
              </button>
            </div>
          </div>
          <div class="search-item">
            <div class="input-group">
              <span class="input-group-addon title">검색어</span>
              <input v-model="search.keyword" type="text" class="form-control"
                     placeholder="사번·이름·URI" @keyup.enter="onSearch()">
            </div>
          </div>
        </div>

        <div class="row mt-10">
          <div class="search-item">
            <button class="btn btn-md line-1" @click="resetSearch()">Reset</button>
          </div>
          <div class="search-item">
            <button class="btn btn-md blue" @click="onSearch()" :disabled="loading">
              {{ loading ? '조회 중...' : '검색' }}
            </button>
          </div>
          <div class="search-item">
            <button class="btn btn-md line-2" @click="exportExcel()" :disabled="!rows.length">
              <i class="icon-download mr-3"></i>엑셀
            </button>
          </div>
        </div>
      </div>

      <!-- 권한자 현황 모드 액션 -->
      <div class="pb-20" v-else>
        <div class="row">
          <div class="search-item">
            <button class="btn btn-md blue" @click="onSearch()" :disabled="loading">{{ loading ? '조회 중...' : '조회' }}</button>
          </div>
          <div class="search-item">
            <button class="btn btn-md line-2" @click="exportExcel()" :disabled="!rows.length">
              <i class="icon-download mr-3"></i>엑셀
            </button>
          </div>
        </div>
      </div>

      <!-- 목록 -->
      <div style="overflow-x:auto;">
        <table class="tbl">
          <thead>
            <tr>
              <th v-for="c in columns" :key="c.key">{{ c.label }}</th>
              <th v-if="mode==='list'" class="text-center">상세</th>
            </tr>
          </thead>
          <tbody v-if="rows.length === 0">
            <tr><td :colspan="columns.length + (mode==='list'?1:0)" class="text-center text-888 pv-30">조회 결과가 없습니다.</td></tr>
          </tbody>
          <tbody v-else>
            <template v-for="(r, i) in rows">
              <tr :key="rowKey(r, i)">
                <td v-for="c in columns" :key="c.key" :class="{ ellipsis: c.ellipsis }" :title="c.ellipsis ? cell(r,c) : null">{{ cell(r, c) }}</td>
                <td v-if="mode==='list'" class="text-center">
                  <button type="button" class="btn btn-xs line-2" @click="toggleDetail(r.id)">{{ expanded[r.id] ? '접기' : '보기' }}</button>
                </td>
              </tr>
              <tr v-if="mode==='list' && expanded[r.id]" :key="'d-'+r.id" class="al-detail-row">
                <td :colspan="columns.length + 1">
                  <table class="tbl v-tbl al-detail">
                    <colgroup><col width="140px"><col></colgroup>
                    <tbody>
                      <tr><th>작업 코드</th><td>{{ r.action || '-' }}</td></tr>
                      <tr><th>결과 메시지</th><td class="al-pre">{{ r.resultMessage || '-' }}</td></tr>
                      <tr><th>Query String</th><td class="al-pre">{{ r.queryString || '-' }}</td></tr>
                      <tr><th>Request Body</th><td class="al-pre">{{ r.requestBody || '-' }}</td></tr>
                      <tr><th>Referer</th><td class="al-pre">{{ r.referer || '-' }}</td></tr>
                      <tr><th>User-Agent</th><td class="al-pre">{{ r.userAgent || '-' }}</td></tr>
                      <tr><th>OS / Host</th><td>{{ (r.os || '-') }} / {{ (r.restHost || '-') }}</td></tr>
                    </tbody>
                  </table>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- 페이지네이션 (목록 모드) -->
      <div class="mt-20" v-if="mode==='list' && total > 0">
        <ul class="pagination" style="justify-content:center;">
          <li :class="{ disabled: page <= 1 }"><a href="javascript:void(0)" @click="goPage(1)"><i class="icon-arrow-line-left-first"></i></a></li>
          <li :class="{ disabled: page <= 1 }"><a href="javascript:void(0)" @click="goPage(page - 10)"><i class="icon-arrow-2line-left"></i></a></li>
          <li :class="{ disabled: page <= 1 }"><a href="javascript:void(0)" @click="goPage(page - 1)"><i class="icon-arrow-line-left-centered"></i></a></li>
          <li v-for="p in pageNumbers" :key="'pg-'+p" :class="{ active: p === page }"><a href="javascript:void(0)" @click="goPage(p)">{{ p }}</a></li>
          <li :class="{ disabled: page >= totalPages }"><a href="javascript:void(0)" @click="goPage(page + 1)"><i class="icon-arrow-line-right-centered"></i></a></li>
          <li :class="{ disabled: page >= totalPages }"><a href="javascript:void(0)" @click="goPage(page + 10)"><i class="icon-arrow-2line-right"></i></a></li>
          <li :class="{ disabled: page >= totalPages }"><a href="javascript:void(0)" @click="goPage(totalPages)"><i class="icon-arrow-line-right-end"></i></a></li>
        </ul>
        <div class="text-center text-888 mt-5" style="font-size:12px;">전체 {{ total }}건</div>
      </div>
      <div class="mt-10 text-center text-888" style="font-size:12px;" v-else-if="rows.length">전체 {{ rows.length }}건</div>
    </div>
  </div>
</template>

<script>
import * as XLSX from 'xlsx';
import moment from 'moment';
import PageHeader from '@/layout/PageHeader.vue';
import Dropdown from '@/components/Dropdown.vue';

const DEFAULT_PAGE_SIZE = 20;
const ALL_VALUE = '__ALL__';
const SENSITIVE = ['TODAY_VISITOR', 'SEARCH_VISITOR', 'SEARCH_HISTORY', 'DOWNLOAD_HISTORY', 'DOWNLOAD_VISITOR'];
const DOWNLOADS = ['DOWNLOAD_HISTORY', 'DOWNLOAD_VISITOR'];

const LIST_COLS = [
  { key: 'timestamp', label: '일시', ts: true },
  { key: 'description', label: '작업', fallback: 'action', ellipsis: true },
  { key: 'empNo', label: '사번' },
  { key: 'empName', label: '이름' },
  { key: 'result', label: '결과' },
  { key: 'method', label: '메서드' },
  { key: 'uri', label: 'URI', ellipsis: true },
  { key: 'ipAddress', label: 'IP' }
];
const FREQ_COLS = [
  { key: 'bucket', label: '시간대' },
  { key: 'action', label: '작업' },
  { key: 'empNo', label: '사번' },
  { key: 'empName', label: '이름' },
  { key: 'description', label: '설명', ellipsis: true },
  { key: 'cnt', label: '횟수' }
];
const USERLEVEL_COLS = [
  { key: 'empHrCorpName', label: '법인' },
  { key: 'empTeam', label: '팀' },
  { key: 'cnt', label: '인원수' }
];

export default {
  name: 'AccessLogList',
  components: { PageHeader, Dropdown },
  data() {
    return {
      mode: 'list',
      dateRange: [new Date(), new Date()], // 기본: 오늘 하루
      selectedActions: [],
      actionDs: [],
      actionModel: '',
      lastChecked: [],
      syncingActions: false,
      offHours: false,
      holidays: '',
      holidayLoading: false,
      search: { keyword: '', result: '' },
      freq: { unit: 'minute', threshold: 30 },
      rows: [],
      total: 0,
      page: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      loading: false,
      expanded: {},
      modeOptions: [
        { name: '로그 목록', value: 'list' },
        { name: '빈도 초과 집계', value: 'freq' },
        { name: '권한자 현황', value: 'userLevels' }
      ],
      unitOptions: [
        { name: '분당', value: 'minute' },
        { name: '시간당', value: 'hour' },
        { name: '일당', value: 'day' }
      ],
      resultOptions: [
        { name: '전체', value: '' },
        { name: '성공(OK)', value: 'OK' },
        { name: '실패(ERROR)', value: 'ERROR' }
      ]
    };
  },
  computed: {
    isAllChecked() {
      const all = this.actionDs.find((x) => x.value === ALL_VALUE);
      return !!(all && all.checked);
    },
    columns() {
      if (this.mode === 'freq') return FREQ_COLS;
      if (this.mode === 'userLevels') return USERLEVEL_COLS;
      return LIST_COLS;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.pageSize));
    },
    pageNumbers() {
      const total = this.totalPages;
      const current = this.page;
      const w = 10;
      if (total <= w) return Array.from({ length: total }, (_, i) => i + 1);
      let start = Math.max(1, current - Math.floor(w / 2));
      let end = start + w - 1;
      if (end > total) { end = total; start = Math.max(1, end - w + 1); }
      const out = [];
      for (let i = start; i <= end; i += 1) out.push(i);
      return out;
    }
  },
  mounted() {
    Promise.all([this.loadActions(), this.fetch()]);
  },
  methods: {
    async loadActions() {
      try {
        const res = await this.$http.get('/api/private/access-log/actions');
        const data = (res && res.data && (res.data.payload || res.data)) || [];
        const list = Array.isArray(data) ? data : [];
        // 첫 항목 '전체'(기본 체크). 개별 action 들과 상호배타.
        this.actionDs = [{ label: '전체', value: ALL_VALUE, checked: true }]
          .concat(list.map((a) => ({ label: a, value: a, checked: false })));
        this.lastChecked = [ALL_VALUE];
        this.selectedActions = [];
        this.syncActionDropdown();
      } catch (e) {
        console.log('access-log actions 로드 실패', e);
      }
    },
    // 체크 변경 시: 전체 ↔ 개별 상호배타, 아무것도 없으면 전체 자동 체크
    onActionCheck() {
      if (this.syncingActions) return;
      const all = this.actionDs.find((x) => x.value === ALL_VALUE);
      const cur = this.actionDs.filter((x) => x.checked).map((x) => x.value);
      const added = cur.filter((v) => this.lastChecked.indexOf(v) < 0);
      if (added.indexOf(ALL_VALUE) >= 0) {
        this.actionDs.forEach((x) => { if (x.value !== ALL_VALUE) x.checked = false; }); // 전체 선택 → 개별 해제
      } else if (added.length > 0 && all) {
        all.checked = false; // 개별 선택 → 전체 해제
      }
      if (!this.actionDs.some((x) => x.checked) && all) all.checked = true; // 아무것도 없으면 전체
      this.lastChecked = this.actionDs.filter((x) => x.checked).map((x) => x.value);
      this.selectedActions = this.actionDs.filter((x) => x.checked && x.value !== ALL_VALUE).map((x) => x.value);
      // 강제 변경분을 드롭다운에 반영 (재귀 방지)
      this.syncingActions = true;
      this.$nextTick(() => {
        const d = this.$refs.actionDropdown;
        if (d && d.changeCheckedItems) d.changeCheckedItems();
        this.$nextTick(() => { this.syncingActions = false; });
      });
    },
    setActions(kind) {
      const allOn = (kind === 'all');
      let target = [];
      if (kind === 'sensitive') target = SENSITIVE;
      else if (kind === 'download') target = DOWNLOADS;
      this.actionDs.forEach((x) => {
        x.checked = (x.value === ALL_VALUE) ? allOn : (!allOn && target.indexOf(x.value) >= 0);
      });
      this.lastChecked = this.actionDs.filter((x) => x.checked).map((x) => x.value);
      this.selectedActions = this.actionDs.filter((x) => x.checked && x.value !== ALL_VALUE).map((x) => x.value);
      this.syncActionDropdown();
    },
    // 드롭다운 내부 라벨/체크상태를 datasource.checked 기준으로 재동기화
    syncActionDropdown() {
      this.syncingActions = true;
      this.$nextTick(() => {
        const d = this.$refs.actionDropdown;
        if (d && typeof d.changeCheckedItems === 'function') d.changeCheckedItems();
        this.$nextTick(() => { this.syncingActions = false; });
      });
    },
    actionsParam() {
      if (this.isAllChecked) return ''; // 전체 → 필터 없음
      return this.selectedActions.length ? this.selectedActions.join(',') : '';
    },
    dateParams() {
      const p = {};
      if (this.dateRange && this.dateRange[0]) p.fromDate = new Date(this.dateRange[0]).setHours(0, 0, 0, 0);
      if (this.dateRange && this.dateRange[1]) p.toDate = new Date(this.dateRange[1]).setHours(23, 59, 59, 999);
      return p;
    },
    changeMode(v) {
      if (this.mode === v) return;
      this.mode = v;
      this.page = 1;
      this.rows = [];
      this.total = 0;
      this.expanded = {};
      this.fetch();
    },
    onSearch() {
      this.page = 1;
      this.fetch();
    },
    resetSearch() {
      this.dateRange = [new Date(), new Date()]; // 기본: 오늘 하루
      this.actionDs.forEach((x) => { x.checked = x.value === ALL_VALUE; }); // 전체로 리셋
      this.lastChecked = [ALL_VALUE];
      this.selectedActions = [];
      this.syncActionDropdown();
      this.offHours = false;
      this.holidays = '';
      this.search = { keyword: '', result: '' };
      this.freq = { unit: 'minute', threshold: 30 };
      this.page = 1;
      this.fetch();
    },
    async fetchHolidays() {
      // 조회 기간의 연도(없으면 올해)로 공휴일 자동 조회 → 입력란에 채움
      const yset = new Set();
      if (this.dateRange && this.dateRange[0]) yset.add(new Date(this.dateRange[0]).getFullYear());
      if (this.dateRange && this.dateRange[1]) yset.add(new Date(this.dateRange[1]).getFullYear());
      if (!yset.size) yset.add(new Date().getFullYear());
      this.holidayLoading = true;
      try {
        const res = await this.$http.get('/api/private/access-log/holidays', { params: { years: [...yset].join(',') } });
        const data = (res && res.data && (res.data.payload || res.data)) || [];
        const list = Array.isArray(data) ? data : [];
        // 기존 입력값과 합쳐 중복 제거
        const merged = new Set(this.holidays.split(',').map((s) => s.trim()).filter(Boolean));
        list.forEach((d) => merged.add(d));
        this.holidays = [...merged].sort().join(',');
      } catch (e) {
        console.log('공휴일 조회 실패', e);
      } finally {
        this.holidayLoading = false;
      }
    },
    async fetch() {
      this.loading = true;
      try {
        if (this.mode === 'freq') await this.fetchFreq();
        else if (this.mode === 'userLevels') await this.fetchUserLevels();
        else await this.fetchList();
      } catch (e) {
        console.log('access-log 조회 실패', e);
        this.rows = []; this.total = 0;
        if (e && e.response && e.response.status === 403) alert('접근 권한이 없습니다. (시스템관리자·보안담당자 전용)');
      } finally {
        this.loading = false;
      }
    },
    async fetchList() {
      const params = Object.assign({ page: this.page, pageSize: this.pageSize }, this.dateParams());
      const acts = this.actionsParam();
      if (acts) params.actions = acts;
      if (this.search.result) params.result = this.search.result;
      if (this.search.keyword) params.keyword = this.search.keyword;
      if (this.offHours) { params.offHours = true; if (this.holidays) params.holidays = this.holidays; }
      const res = await this.$http.get('/api/private/access-log/list', { params });
      const data = (res && res.data && (res.data.payload || res.data)) || {};
      this.rows = data.rows || [];
      this.total = data.total || 0;
      this.page = data.page || this.page;
      this.pageSize = data.pageSize || this.pageSize;
      this.expanded = {};
    },
    async fetchFreq() {
      const params = Object.assign({ unit: this.freq.unit, threshold: this.freq.threshold }, this.dateParams());
      const acts = this.actionsParam();
      if (acts) params.actions = acts;
      const res = await this.$http.get('/api/private/access-log/report/frequency', { params });
      const data = (res && res.data && (res.data.payload || res.data)) || [];
      this.rows = Array.isArray(data) ? data : [];
      this.total = this.rows.length;
    },
    async fetchUserLevels() {
      const res = await this.$http.get('/api/private/access-log/report/user-levels');
      const data = (res && res.data && (res.data.payload || res.data)) || [];
      this.rows = Array.isArray(data) ? data : [];
      this.total = this.rows.length;
    },
    goPage(p) {
      const target = Math.min(Math.max(1, p), this.totalPages);
      if (target === this.page) return;
      this.page = target;
      this.fetch();
    },
    toggleDetail(id) {
      this.$set(this.expanded, id, !this.expanded[id]);
    },
    rowKey(r, i) {
      return (r && r.id != null) ? r.id : 'r-' + i;
    },
    cell(row, col) {
      let v = row[col.key];
      if ((v == null || v === '') && col.fallback) v = row[col.fallback];
      if (col.ts) return this.fmtTs(v);
      return v == null ? '' : String(v);
    },
    fmtTs(v) {
      if (v == null || v === '') return '';
      if (Array.isArray(v)) {
        const [y, mo, d, h = 0, mi = 0, s = 0] = v;
        return moment({ year: y, month: (mo || 1) - 1, day: d, hour: h, minute: mi, second: s }).format('YYYY-MM-DD HH:mm:ss');
      }
      const m = moment(v);
      return m.isValid() ? m.format('YYYY-MM-DD HH:mm:ss') : String(v);
    },
    exportExcel() {
      if (!this.rows.length) return;
      const header = this.columns.map((c) => c.label);
      const body = this.rows.map((r) => this.columns.map((c) => this.cell(r, c)));
      const sheet = XLSX.utils.aoa_to_sheet([header, ...body]);
      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book, sheet, 'log');
      const modeName = this.mode === 'freq' ? '빈도초과' : (this.mode === 'userLevels' ? '권한자현황' : '로그목록');
      XLSX.writeFile(book, `로그_${modeName}_${moment().format('YYYYMMDD_HHmm')}.xlsx`);
    }
  }
};
</script>

<style scoped>
.al-detail-row > td { background: #fafbfc; padding: 10px 14px; }
.al-detail th { background: #f5f5f5; text-align: left; vertical-align: top; }
.al-pre { white-space: pre-wrap; word-break: break-all; font-size: 12px; }
</style>